import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { publicApi } from '../utils/api';
import { IconBroadcast, IconCalendar, IconTrophy, IconFish } from '../components/Icons';
import { eventStatusLabel } from '../utils/eventStatus';

export default function PublicEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { siteName, logoUrl } = useTheme();
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    publicApi.getEvents()
      .then(res => setEvents(res.data.data || []))
      .catch(err => console.error('Failed to fetch events:', err))
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();

  const current = events.filter(ev => {
    if (ev.status === 'live') return true;
    if (ev.status === 'active') {
      const start = new Date(ev.start_date);
      const end = new Date(ev.end_date);
      return start <= now && now <= end;
    }
    return false;
  });

  const upcoming = events.filter(ev => {
    if (ev.status !== 'active') return false;
    return new Date(ev.start_date) > now;
  });

  const past = events.filter(ev => ev.status === 'completed');

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ── Navbar ──────────────────────────────────────────── */}
      <nav className="navbar">
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={siteName} style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
            ) : (
              <IconFish size="24px" />
            )}
          </Link>

          <div className="navbar-links">
            <Link to="/public/events" className="navbar-link active">Events</Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn btn-primary btn-sm">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </div>

          <button
            className="navbar-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`navbar-hamburger-icon${menuOpen ? ' open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      <div
        className={`navbar-overlay${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div className={`navbar-drawer${menuOpen ? ' open' : ''}`}>
        <div className="navbar-drawer-links">
          <Link to="/public/events" className="navbar-drawer-link active" onClick={() => setMenuOpen(false)}>Events</Link>
          {isAuthenticated ? (
            <Link to="/dashboard" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      <div className="page" style={{ flex: 1 }}>
        <div className="page-content">
          <div style={{ marginBottom: 16 }}>
            <Link to="/" style={{ color: 'var(--amber)', fontSize: '0.85rem', textDecoration: 'none' }}>
              &larr; Back to Home
            </Link>
          </div>

          <div className="page-header">
            <h1>All Events</h1>
            <p style={{ color: 'var(--text-secondary)' }}>Browse current, upcoming, and past fishing events</p>
          </div>

          {loading ? (
            <div className="spinner" />
          ) : (
            <>
              <EventSection title="Current Events" icon={<IconBroadcast size="16px" />} events={current} emptyText="No events happening right now" />
              <EventSection title="Upcoming Events" icon={<IconCalendar size="16px" />} events={upcoming} emptyText="No upcoming events scheduled" />
              <EventSection title="Past Events" icon={<IconTrophy size="16px" />} events={past} emptyText="No past events" />
            </>
          )}
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer style={{
        padding: '24px 20px',
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: '0.82rem',
        borderTop: '1px solid var(--border)',
      }}>
        {siteName} &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function EventSection({ title, icon, events, emptyText }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>{icon}{title}</h3>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
          {events.length} event{events.length !== 1 ? 's' : ''}
        </span>
      </div>
      {events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-secondary)' }}>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {events.map(ev => (
            <EventCard key={ev.id} event={ev} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ event }) {
  const startDate = new Date(event.start_date).toLocaleDateString();
  const endDate = new Date(event.end_date).toLocaleDateString();
  const dateRange = startDate === endDate ? startDate : `${startDate} – ${endDate}`;

  return (
    <Link
      to={`/public/events/${event.id}`}
      className="event-card-row"
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px', background: 'var(--surface)', borderRadius: 10,
        border: '1px solid var(--border)', textDecoration: 'none',
        transition: 'all 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--amber)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      <div>
        <div style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: 2 }}>{event.name}</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
          {event.location || 'No location'} · {dateRange}
        </div>
        {event.organizer_name && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 2 }}>
            by {event.organizer_name}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {event.tournament_count > 0 && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            {event.tournament_count} tournament{event.tournament_count !== 1 ? 's' : ''}
          </span>
        )}
        {event.event_type && (
          <span className="badge badge-active" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
            {event.event_type}
          </span>
        )}
        <span className={`badge badge-${event.status}`}>{eventStatusLabel(event.status)}</span>
      </div>
    </Link>
  );
}
